/**
 * useReportContext
 *
 * Returns a `build()` function that assembles the report context payload.
 * Collects: user info, current thread, route, viewport, JS errors, build info.
 *
 * GDPR: Only includes thread messages if user explicitly grants consent (consentGiven).
 * The message preview is capped at 500 chars per message for data minimization.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThread } from '../context/ThreadContext';
import { getErrorBuffer } from '../utils/errorBuffer';
import { detectArea } from '../components/reports/AreaAutoDetect';

/**
 * Hook that returns a context builder function for report submissions.
 * @returns {{ build: () => Object, detectCurrentArea: () => string }}
 */
export function useReportContext() {
  useAuth(); // subscribes to auth context; user/profile available if needed
  const { currentThread } = useThread();
  const location = useLocation();

  /**
   * Builds the context object to attach to a report.
   * @returns {Object} context payload
   */
  const build = useCallback(() => {
    const ctx = {
      // Route info
      route: location.pathname + (location.search || ''),

      // Build info (injected by build script)
      gitSha: process.env.REACT_APP_GIT_SHA || null,
      buildTime: process.env.REACT_APP_BUILD_TIME || null,

      // Browser / viewport
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      language: navigator.language,

      // Thread context
      threadId: currentThread?.id || null,
      threadTitle: currentThread?.title || null,

      // JS errors captured by errorBuffer
      recentErrors: getErrorBuffer(),
    };

    return ctx;
  }, [location, currentThread]);

  /**
   * Returns the area auto-detected from the current route.
   * @returns {string}
   */
  const detectCurrentArea = useCallback(() => {
    return detectArea(location.pathname);
  }, [location.pathname]);

  return { build, detectCurrentArea };
}
